// src/lib/auth.jsx
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth, db } from './firebase';
import { query, where, limit, getDocsFromCache, onSnapshot } from 'firebase/firestore';
import { eventCollection, eventDoc } from '../event/paths';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import NotAuthorized from '../pages/NotAuthorized';
import { normalizeRole, ROLES } from './auth/roles';
import { useEvent } from '../event/eventContext';

const AuthCtx = createContext(null);
const PROFILE_RETRY_MS = 750;
const PROFILE_MAX_RETRIES = 20;
const ROLE_PRIORITY = [
  ROLES.ADMIN,
  ROLES.MANAGEMENT,
  ROLES.HEAD_HOST,
  ROLES.AMBASSADOR,
  ROLES.HOST,
  ROLES.DJ,
  ROLES.EMERSON,
  ROLES.YOURS_TRULY,
  ROLES.BAR_BAMBI
];

function isActiveStatus(value) {
  return (
    String(value || '')
      .trim()
      .toLowerCase() === 'active'
  );
}

function primaryMemberRole(member) {
  const roles = Array.isArray(member?.roles) ? member.roles.map((role) => normalizeRole(role)).filter(Boolean) : [];
  if (!roles.length) return null;
  return ROLE_PRIORITY.find((role) => roles.includes(role)) || roles[0];
}

export function AuthProvider({ children }) {
  const { brand } = useEvent();
  const brandName = brand?.name || brand?.shortName || 'this brand';

  // undefined = loading; null = signed out; object = signed in
  const [user, setUser] = useState(undefined);

  // 🔹 Brand membership = events/{eventId}/members/{uid}; this is the platform access gate.
  const [member, setMember] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberReady, setMemberReady] = useState(false);
  const [memberError, setMemberError] = useState(null);

  // 🔹 Base profile = your own Firestore user doc
  const [baseProfile, setBaseProfile] = useState(null);
  const [baseProfileLoading, setBaseProfileLoading] = useState(false);
  const [baseProfileReady, setBaseProfileReady] = useState(false);

  // 🔹 Impersonation state (admin “view as”)
  const [impersonatedProfile, setImpersonatedProfile] = useState(null);

  // 🔐 Platform superadmin = Auth custom claim `superadmin:true` (NOT a Firestore role — the
  // `admin` role does not imply it). Read from the ID token. null = not resolved yet; {} = no
  // claims; object = claims. Guards/menu wait on null so they don't flash "not authorized".
  const [claims, setClaims] = useState(null);
  const isSuperadmin = useMemo(() => claims?.superadmin === true, [claims]);

  // Persist auth session in local storage + auth listener
  useEffect(() => {
    let unsubAuth = null;
    let mounted = true;

    (async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch {
        // ignore (Safari private mode can throw)
      }

      // Attach listener after persistence attempt
      unsubAuth = onAuthStateChanged(auth, (u) => {
        if (!mounted) return;

        setUser(u || null);

        // When auth user changes (or logs out), clear impersonation + profiles
        if (!u) {
          setClaims({});
          setMember(null);
          setMemberLoading(false);
          setMemberReady(false);
          setMemberError(null);
          setBaseProfile(null);
          setBaseProfileLoading(false);
          setBaseProfileReady(false);
          setImpersonatedProfile(null);
        } else {
          setClaims(null);
          setMember(null);
          setMemberLoading(false);
          setMemberReady(false);
          setMemberError(null);
          setBaseProfile(null);
          setBaseProfileLoading(false);
          setBaseProfileReady(false);
          setImpersonatedProfile(null);
        }
      });
    })();

    return () => {
      mounted = false;
      try {
        if (typeof unsubAuth === 'function') unsubAuth();
      } catch {
        // ignore
      }
    };
  }, []);

  // Resolve brand membership after claims are known. Superadmin access is claim-based and
  // intentionally does not require a per-brand members/{uid} doc.
  useEffect(() => {
    if (!user) {
      setMember(null);
      setMemberLoading(false);
      setMemberReady(false);
      setMemberError(null);
      return undefined;
    }

    if (claims === null) {
      setMember(null);
      setMemberLoading(false);
      setMemberReady(false);
      setMemberError(null);
      return undefined;
    }

    if (isSuperadmin) {
      setMember(null);
      setMemberLoading(false);
      setMemberReady(true);
      setMemberError(null);
      return undefined;
    }

    const ref = eventDoc(db, 'members', user.uid);
    let active = true;
    let unsub = null;
    let retryTimer = null;
    let retryCount = 0;
    setMember(null);
    setMemberLoading(true);
    setMemberReady(false);
    setMemberError(null);

    const clearSubscription = () => {
      try {
        if (typeof unsub === 'function') unsub();
      } catch {
        // ignore
      }
      unsub = null;
    };

    // Same retry as the profile listener: a dropped connection or a sign-in token that is
    // mid-refresh should not leave the host stuck on the "could not confirm" screen.
    // While retrying, a cached membership keeps showing (no flicker); with no cache the
    // route guard keeps its spinner because memberReady is still false.
    const scheduleRetry = (err) => {
      const retryable = ['permission-denied', 'unavailable', 'deadline-exceeded'].includes(err?.code);
      if (!retryable || retryCount >= PROFILE_MAX_RETRIES) {
        setMember(null);
        setMemberError(err);
        setMemberLoading(false);
        setMemberReady(true);
        return;
      }

      retryCount += 1;
      setMemberLoading(true);
      retryTimer = setTimeout(() => {
        if (active) subscribe();
      }, PROFILE_RETRY_MS);
    };

    const subscribe = () => {
      clearSubscription();
      unsub = onSnapshot(
        ref,
        { includeMetadataChanges: true },
        (snap) => {
          if (!active) return;

          // Only a server answer proves the read works. Each resubscribe replays the cache
          // first, so resetting on a cached snapshot would let the retries run forever.
          if (!snap.metadata.fromCache) retryCount = 0;

          if (snap.exists()) {
            setMember({ id: snap.id, ...snap.data() });
            setMemberError(null);
            setMemberLoading(false);
            setMemberReady(true);
            return;
          }

          setMember(null);

          // IndexedDB can produce an empty cache miss before the server response.
          if (!snap.metadata.fromCache) {
            setMemberError(null);
            setMemberLoading(false);
            setMemberReady(true);
          }
        },
        (err) => {
          console.error('Membership snapshot error:', err);
          if (!active) return;
          clearSubscription();
          scheduleRetry(err);
        }
      );
    };

    subscribe();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      clearSubscription();
    };
  }, [user, claims, isSuperadmin]);

  // Prefetch profile by userUID (cache first, then live)
  useEffect(() => {
    const canLoadProfile = !!user && (isSuperadmin || (memberReady && isActiveStatus(member?.status)));

    if (!user) {
      setBaseProfile(null);
      setBaseProfileLoading(false);
      setBaseProfileReady(false);
      return undefined;
    }

    if (!canLoadProfile) {
      setBaseProfile(null);
      setBaseProfileLoading(false);
      setBaseProfileReady(false);
      return undefined;
    }

    const qRef = query(eventCollection(db, 'users'), where('userUID', '==', user.uid), limit(1));
    let active = true;
    let unsub = null;
    let retryTimer = null;
    let retryCount = 0;

    const clearSubscription = () => {
      try {
        if (typeof unsub === 'function') unsub();
      } catch {
        // ignore
      }
      unsub = null;
    };

    const scheduleRetry = (err) => {
      const retryable = ['permission-denied', 'unavailable', 'deadline-exceeded'].includes(err?.code);
      if (!retryable || retryCount >= PROFILE_MAX_RETRIES) {
        setBaseProfileLoading(false);
        setBaseProfileReady(true);
        return;
      }

      retryCount += 1;
      setBaseProfileLoading(true);
      retryTimer = setTimeout(() => {
        if (active) subscribe();
      }, PROFILE_RETRY_MS);
    };

    const subscribe = () => {
      clearSubscription();
      unsub = onSnapshot(
        qRef,
        { includeMetadataChanges: true },
        (qsnap) => {
          if (!active) return;
          const d = qsnap.docs[0];

          if (d) {
            retryCount = 0;
            setBaseProfile({ id: d.id, ...d.data() });
            setBaseProfileLoading(false);
            setBaseProfileReady(true);
            return;
          }

          setBaseProfile(null);

          // Firestore persistence can emit a cache-only empty result before the server has answered.
          // Keep waiting so a new onboarded host does not briefly look role-less.
          if (!qsnap.metadata.fromCache) {
            setBaseProfileLoading(false);
            setBaseProfileReady(true);
          }
        },
        (err) => {
          console.error('Profile (userUID) snapshot error:', err);
          if (!active) return;
          clearSubscription();
          scheduleRetry(err);
        }
      );
    };

    // 1) Instant paint from cache if available
    (async () => {
      try {
        const cacheSnap = await getDocsFromCache(qRef);
        const d0 = cacheSnap.docs[0];
        if (active && d0) {
          setBaseProfile({ id: d0.id, ...d0.data() });
          setBaseProfileLoading(false);
          setBaseProfileReady(true);
        }
      } catch {
        // no cache yet, ignore
      }
    })();

    // 2) Live subscription
    setBaseProfileLoading(true);
    setBaseProfileReady(false);
    subscribe();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      clearSubscription();
    };
  }, [user, isSuperadmin, memberReady, member?.status]);

  // Resolve the superadmin custom claim from the ID token whenever the auth user changes.
  // Uses the cached token (no forced refresh) — the operator signs out/in after a claim change
  // to pick it up, which is the existing flow. A doc read isn't possible: superadmin is not a
  // Firestore field, it's an Auth claim that rules.v2 checks via request.auth.token.superadmin.
  useEffect(() => {
    let active = true;
    if (!user) {
      setClaims(user === undefined ? null : {}); // loading -> null; signed out -> {}
      return undefined;
    }
    // Reset to null (unresolved) the moment a user arrives, BEFORE the async token read — else a
    // stale `{}` from a prior signed-out state would make SuperadminRoute treat claims as resolved
    // and redirect to /not-authorized before getIdTokenResult() returns (Codex Med).
    setClaims(null);
    user
      .getIdTokenResult()
      .then((res) => {
        if (active) setClaims(res?.claims || {});
      })
      .catch(() => {
        if (active) setClaims({});
      });
    return () => {
      active = false;
    };
  }, [user]);

  const handleSignOut = () => fbSignOut(auth);

  // 👇 Effective profile = impersonated user OR your own profile
  const effectiveProfile = useMemo(() => impersonatedProfile || baseProfile, [impersonatedProfile, baseProfile]);

  // Menu-visibility superadmin: drop the platform privilege while impersonating so the nav
  // previews exactly what the impersonated user sees. `isSuperadmin` (the raw token claim) stays
  // authoritative for SECURITY gates (route guards, callable auth) — only menu rendering uses this.
  const isSuperadminEffective = useMemo(() => isSuperadmin && !impersonatedProfile, [isSuperadmin, impersonatedProfile]);
  const memberRoleForAccess = useMemo(() => primaryMemberRole(member), [member]);

  // 🔑 Stable selectors for downstream queries (using effective profile)
  const userIdForGuestlists = useMemo(() => {
    if (!effectiveProfile) return null;
    return effectiveProfile.userId ?? effectiveProfile.id ?? null;
  }, [effectiveProfile]);

  const roleForAccess = useMemo(() => {
    if (impersonatedProfile) return impersonatedProfile.type ?? impersonatedProfile.role ?? null;
    return memberRoleForAccess ?? effectiveProfile?.type ?? effectiveProfile?.role ?? null;
  }, [effectiveProfile, impersonatedProfile, memberRoleForAccess]);

  const needsBrandProfile = !!user && !isSuperadmin && isActiveStatus(member?.status);
  const claimsLoading = !!user && claims === null;
  const membershipLoading = !!user && !isSuperadmin && claims !== null && !memberReady;
  const profileLoading = needsBrandProfile && (!baseProfileReady || (baseProfileLoading && !effectiveProfile));
  const loading = user === undefined || claimsLoading || membershipLoading || profileLoading;

  const accessBlocked = useMemo(() => {
    if (!user || user === undefined || loading || isSuperadmin) return null;

    if (memberError) {
      return {
        title: 'We could not check your access',
        message: `Your sign-in may have expired, or your connection dropped while the ${brandName} portal was loading.`,
        detail: 'Tap Sign out below, then sign back in. If you still see this after that, speak with your management team.'
      };
    }

    if (!member) {
      return {
        title: `No access to ${brandName}`,
        message: `You do not currently have access to the ${brandName} portal.`,
        detail: 'Please speak with your management team if you think you should have access.'
      };
    }

    if (!isActiveStatus(member.status)) {
      return {
        title: `No access to ${brandName}`,
        message: `Your access to the ${brandName} portal is currently inactive.`,
        detail: 'Please speak with your management team if you need access again.'
      };
    }

    if (!baseProfile) {
      return {
        title: `No access to ${brandName}`,
        message: `Your account is not fully set up for the ${brandName} portal.`,
        detail: 'Please speak with your management team so they can finish setting up your access.'
      };
    }

    if (baseProfile.status && !isActiveStatus(baseProfile.status)) {
      return {
        title: `No access to ${brandName}`,
        message: `Your access to the ${brandName} portal is currently inactive.`,
        detail: 'Please speak with your management team if you need access again.'
      };
    }

    return null;
  }, [user, loading, isSuperadmin, memberError, member, baseProfile, brandName]);

  return (
    <AuthCtx.Provider
      value={{
        user,
        member,
        memberLoading,
        memberReady,
        memberError,
        profile: effectiveProfile,
        realProfile: baseProfile,
        claims,
        isSuperadmin,
        isSuperadminEffective,
        impersonatedProfile,
        setImpersonatedProfile,
        userIdForGuestlists,
        roleForAccess,
        accessBlocked,
        profileLoading,
        loading,
        signOut: handleSignOut,
        logout: handleSignOut
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

/**
 * Route guard.
 * Important: do NOT redirect while auth is still resolving (loading === true),
 * otherwise cross-tab auth sync can bounce users to /login then /dashboard.
 */
export function RequireAuth({ children, redirectTo = '/login' }) {
  const { user, loading, profile, isSuperadmin, accessBlocked, signOut } = useAuth();
  const location = useLocation();
  const waitUntilRef = useRef(null);
  const [, forceRerender] = useState(0);

  // Start grace window the first render where user is set but profile hasn't arrived yet
  if (!loading && user && !isSuperadmin && !accessBlocked && !profile && !waitUntilRef.current) {
    waitUntilRef.current = Date.now() + 5000;
  }
  // Clear grace window once profile arrives
  if ((profile || isSuperadmin || accessBlocked) && waitUntilRef.current) {
    waitUntilRef.current = null;
  }

  // Re-render when the grace window expires so we stop blocking
  useEffect(() => {
    if (!waitUntilRef.current) return;
    const ms = Math.max(0, waitUntilRef.current - Date.now());
    const t = setTimeout(() => forceRerender((x) => x + 1), ms);
    return () => clearTimeout(t);
  }, [profile, user, loading]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    const intended = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    try {
      sessionStorage.setItem('postLoginRedirect', intended);
    } catch {
      // ignore
    }
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  if (accessBlocked) {
    return <NotAuthorized {...accessBlocked} fullScreen onSignOut={signOut} />;
  }

  // Hold the dashboard at a spinner until profile loads (or 5s grace expires)
  if (!isSuperadmin && !profile && waitUntilRef.current && Date.now() < waitUntilRef.current) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return children;
}
